/** @shell standard @header bar */
import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, Alert, Image } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { ButtonLoader } from '../components/load-main/ButtonLoader';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { Input } from '../components/common/Input';
import { useAuthStore } from '../shared/auth/store';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { useTranslation } from '../shared/i18n';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioSectionCardStyle,
  androidScaleFont,
  androidScaleSize,
  androidFlatSurfaceStyle,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';
import { DASH_STUDIO_INNER } from '../shared/theme/studio/dashboardConstants';
import {
  buildProfileScreenProfileUpdateFormData,
  deactivateAccount,
  deleteAccount,
  fetchProfileAndSyncUser,
  postCustomerProfileImage,
  postCustomerProfileUpdate,
  resolveProfileFirstName,
  resolveProfileLastName,
} from '../data/domains/profile';
import { isHasLastNameEnabled } from '../data/domains/config';
import { useRequireVerified } from '../shared/auth/useRequireVerified';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useRtl } from '../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../shared/i18n/rtl';
import { hexToRgba } from '../shared/theme/colorUtils';
import { ProfileGenderToggle } from './components/ProfileGenderToggle';

const PROFILE_AVATAR_SIZE = 100;

function createProfileScreenStyles(c: ThemeColors) {
  const avatar_size = androidScaleSize(PROFILE_AVATAR_SIZE);
  const avatar_radius = avatar_size / 2;

  return StyleSheet.create({
    editButtonWrap: {},
    editButtonInner: {},
    editButton: {
      ...appTheme.typography.body,
      fontSize: androidScaleFont(16),
      color: c.primary,
      fontWeight: '600',
    },
    content: {
      flex: 1,
    },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    studioCard: {
      alignSelf: 'stretch',
    },
    avatarSection: {
      alignItems: 'center',
      paddingVertical: appTheme.spacing.xl,
      paddingHorizontal: DASH_STUDIO_INNER,
    },
    avatarContainerWrap: {},
    avatarContainer: {
      position: 'relative',
    },
    avatar: {
      width: avatar_size,
      height: avatar_size,
      borderRadius: avatar_radius,
      backgroundColor: c.primary,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: appTheme.spacing.md,
    },
    avatarImage: {
      width: avatar_size,
      height: avatar_size,
      borderRadius: avatar_radius,
      marginBottom: appTheme.spacing.md,
    },
    cameraOverlay: {
      position: 'absolute',
      bottom: appTheme.spacing.md,
      right: 0,
      left: 0,
      height: androidScaleSize(30),
      backgroundColor: c.backdrop,
      borderBottomLeftRadius: avatar_radius,
      borderBottomRightRadius: avatar_radius,
      justifyContent: 'center',
      alignItems: 'center',
    },
    avatarText: {
      ...appTheme.typography.h1,
      fontSize: androidScaleFont(40),
      color: c.onPrimary,
    },
    changePhotoButtonWrap: {},
    changePhotoButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      borderRadius: DASH_STUDIO_RADIUS,
      ...androidFlatSurfaceStyle(),
    },
    changePhotoText: {
      ...appTheme.typography.body,
      fontSize: androidScaleFont(16),
      color: c.primary,
      marginStart: appTheme.spacing.sm,
      fontWeight: '500',
    },
    form: {
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: appTheme.spacing.lg,
    },
    formFieldsColumn: {
      gap: appTheme.spacing.lg,
      alignSelf: 'stretch',
    },
    inputGroup: {
      alignSelf: 'stretch',
      marginBottom: 0,
    },
    label: {
      ...appTheme.typography.bodySmall,
      fontSize: androidScaleFont(14),
      color: hexToRgba(c.text, 0.72),
      fontWeight: '600',
    },
    genderSection: {
      alignSelf: 'stretch',
      gap: appTheme.spacing.sm,
    },
    saveButtonWrap: {
      alignSelf: 'stretch',
    },
    saveButton: {
      backgroundColor: c.primary,
      paddingVertical: appTheme.spacing.md,
      borderRadius: DASH_STUDIO_RADIUS,
      alignItems: 'center',
      ...androidFlatSurfaceStyle(),
    },
    saveButtonDisabled: {
      opacity: 0.6,
    },
    saveButtonText: {
      ...appTheme.typography.body,
      fontSize: androidScaleFont(16),
      color: c.onPrimary,
      fontWeight: '600',
    },
    nameRow: {
      flexDirection: 'row',
      gap: appTheme.spacing.md,
      alignSelf: 'stretch',
    },
    nameField: {
      flex: 1,
    },
    dangerZone: {
      padding: DASH_STUDIO_INNER,
    },
    dangerZoneTitle: {
      ...appTheme.typography.bodySmall,
      fontSize: androidScaleFont(12),
      fontWeight: '600',
      color: c.error ?? c.primary,
      marginBottom: appTheme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    deactivateAccountButtonWrap: {
      marginBottom: appTheme.spacing.sm,
    },
    deactivateAccountButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.md,
      backgroundColor: c.warning + '10',
      borderRadius: DASH_STUDIO_RADIUS,
      gap: appTheme.spacing.sm,
      ...androidFlatSurfaceStyle(),
    },
    deactivateAccountText: {
      ...appTheme.typography.bodyMedium,
      fontSize: androidScaleFont(16),
      color: c.warning,
    },
    deleteAccountButtonWrap: {},
    deleteAccountButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.md,
      backgroundColor: c.error + '10',
      borderRadius: DASH_STUDIO_RADIUS,
      gap: appTheme.spacing.sm,
      ...androidFlatSurfaceStyle(),
    },
    deleteAccountText: {
      ...appTheme.typography.bodyMedium,
      fontSize: androidScaleFont(16),
      color: c.error,
    },
  });
}

export const ProfileScreen: React.FC = () => {
  const on_back_exit = useExitOrGoBack();
  const { t } = useTranslation();
  const rtl = useRtl();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createProfileScreenStyles(themeColors), [themeColors]);
  const studio_card = useMemo(() => studioSectionCardStyle(themeColors), [themeColors]);
  const header_top_padding = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const insets = useSafeAreaInsets();
  const user = useAuthStore(s => s.user);
  const has_last_name_enabled = isHasLastNameEnabled();

  useRequireVerified('Profile');
  const setUser = useAuthStore(s => s.setUser);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: resolveProfileFirstName(user ?? {}) || '',
    last_name: resolveProfileLastName(user ?? {}) || '',
    mail: user?.mail || (user as any)?.email || '',
    phone: user?.phone || '',
    gender: (user as any)?.gender || 'user',
    height: (user as any)?.height || '',
    weight: (user as any)?.weight || '',
    date_of_birth: (user as any)?.date_of_birth || '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [profileImage, setProfileImage] = useState<string | null>((user as any)?.profile_image ?? (user as any)?.picture ?? null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    if (!user?.id) {return;}
    let cancelled = false;
    fetchProfileAndSyncUser(user.id)
      .then((res: any) => {
        if (!cancelled) {
          const customer = res?.data?.customer ?? res?.customer ?? null;
          setProfile(customer ?? res);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user) {
      return;
    }
    setFormData({
      name: resolveProfileFirstName(user ?? {}) ?? '',
      last_name: resolveProfileLastName(user ?? {}) ?? '',
      mail: user?.mail ?? (user as any)?.email ?? '',
      phone: user?.phone ?? '',
      gender: (user as any)?.gender ?? 'user',
      height: (user as any)?.height ?? '',
      weight: (user as any)?.weight ?? '',
      date_of_birth: (user as any)?.date_of_birth ?? '',
    });
    setProfileImage((user as any)?.profile_image ?? (user as any)?.picture ?? null);
  }, [user]);

  const handlePickImage = async () => {
    try {
      const ImagePicker = require('react-native-image-picker');
      const result = await ImagePicker.launchImageLibrary({
        mediaType: 'photo',
        quality: 0.8,
        maxWidth: 500,
        maxHeight: 500,
      });

      if (result.didCancel || !result.assets?.[0]?.uri) {
        return;
      }

      const imageUri = result.assets[0].uri;
      setIsUploadingImage(true);

      const formDataUpload = new FormData();
      formDataUpload.append('customer_id', user?.id?.toString() || '');
      formDataUpload.append('profile_image', {
        uri: imageUri,
        type: result.assets[0].type || 'image/jpeg',
        name: result.assets[0].fileName || 'profile.jpg',
      } as any);

      const response: any = await postCustomerProfileImage(formDataUpload);

      if (response?.success) {
        const image_uri = response?.data?.customer?.picture ?? response?.data?.profile_image ?? imageUri;
        setProfileImage(image_uri);
        if (response?.data?.customer) {
          setUser(response.data.customer);
        }
        Alert.alert(t('common.success'), t('profile.profilePictureUpdated'));
      } else {
        Alert.alert(t('common.error'), response?.message ?? t('profile.failedToUploadImage'));
      }
    } catch (err: any) {
      Alert.alert(t('common.error'), err?.message ?? t('profile.imagePickerNotAvailable'));
    } finally {
      setIsUploadingImage(false);
    }
  };
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = t('profile.firstNameRequired');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('profile.firstNameMinLength');
    }

    if (!formData.mail.trim()) {
      newErrors.mail = t('profile.mailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.mail)) {
      newErrors.mail = t('profile.invalidEmail');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('profile.phoneRequired');
    } else if (!/^\+?[0-9]{8,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = t('profile.invalidPhone');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!user?.id) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const prefs_raw = (user as any)?.preferences ?? profile?.preferences;
      const fd = buildProfileScreenProfileUpdateFormData(
        user.id,
        {
          first_name: formData.name,
          last_name: has_last_name_enabled ? formData.last_name : formData.name,
          mail: formData.mail,
          phone: formData.phone,
          gender: formData.gender,
          height: formData.height,
          weight: formData.weight,
          date_of_birth: formData.date_of_birth,
        },
        prefs_raw,
      );
      const response: any = await postCustomerProfileUpdate(fd);

      if (response?.success) {
        await fetchProfileAndSyncUser(user.id);
        Alert.alert(t('common.success'), t('profile.profileUpdatedSuccess'));
        setIsEditing(false);
      } else {
        Alert.alert(t('common.error'), response?.message ?? t('profile.failedToUpdateProfile'));
      }
    } catch (err: any) {
      Alert.alert(t('common.error'), err?.message ?? t('profile.failedToUpdateProfile'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StackScreenShell colors={themeColors}>
      <ScreenHeader
        title={t('profile.title')}
        onBack={on_back_exit}
        scrollY={scrollY}
        rightAction={
          <Clickable onPress={() => setIsEditing(!isEditing)} accessibilityLabel={isEditing ? t('common.cancel') : t('profile.edit')} accessibilityRole="button">
            <View style={styles.editButtonWrap}>
              <View style={styles.editButtonInner}>
                <Text style={styles.editButton}>{isEditing ? t('common.cancel') : t('profile.edit')}</Text>
              </View>
            </View>
          </Clickable>
        }
      />

      <Animated.ScrollView
        style={styles.content}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingTop: header_top_padding, paddingBottom: insets.bottom + appTheme.spacing.xl },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={[studio_card, styles.studioCard, styles.avatarSection]}>
          <Clickable
            onPress={isEditing ? handlePickImage : undefined}
            disabled={!isEditing || isUploadingImage}
            accessibilityLabel={t('profile.changePhoto')}
            accessibilityRole="button">
            <View style={styles.avatarContainerWrap}>
              <View style={styles.avatarContainer}>
              {profileImage ? (
                <Image source={{ uri: profileImage }} style={styles.avatarImage} />
              ) : (
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>{formData.name.charAt(0).toUpperCase()}</Text>
                </View>
              )}
              {isEditing && (
                <View style={styles.cameraOverlay}>
                  {isUploadingImage ? <ButtonLoader size={18} color={themeColors.onPrimary} /> : <SvgIcon name="edit" size={20} color={themeColors.onPrimary} />}
                </View>
              )}
              </View>
            </View>
          </Clickable>
          {isEditing && (
            <Clickable onPress={handlePickImage} disabled={isUploadingImage} accessibilityLabel={t('profile.changePhoto')} accessibilityRole="button">
              <View style={styles.changePhotoButtonWrap}>
                <View style={styles.changePhotoButton}>
                <SvgIcon name="edit" size={20} color={themeColors.primary} />
                <Text style={styles.changePhotoText}>{t('profile.changePhoto')}</Text>
                </View>
              </View>
            </Clickable>
          )}
        </View>

        <View style={[studio_card, styles.studioCard, styles.form]}>
          <View style={styles.formFieldsColumn}>
          <View style={[has_last_name_enabled ? styles.nameRow : undefined, horizontalLtrDirectionStyle]}>
            <View style={has_last_name_enabled ? styles.nameField : undefined}>
              <Input
                value={formData.name}
                onChangeText={text => {
                  setFormData({ ...formData, name: text });
                  if (errors.name) {setErrors({ ...errors, name: '' });}
                }}
                editable={isEditing}
                placeholder={t('profile.firstNamePlaceholder')}
                error={errors.name || undefined}
                disabled={!isEditing}
                containerStyle={styles.inputGroup}
              />
            </View>
            {has_last_name_enabled ? (
              <View style={styles.nameField}>
                <Input
                  value={formData.last_name}
                  onChangeText={text => setFormData({ ...formData, last_name: text })}
                  editable={isEditing}
                  placeholder={t('profile.lastNamePlaceholder')}
                  disabled={!isEditing}
                  containerStyle={styles.inputGroup}
                />
              </View>
            ) : null}
          </View>

            <Input
              value={formData.mail}
              onChangeText={text => {
                setFormData({ ...formData, mail: text });
                if (errors.mail) {setErrors({ ...errors, mail: '' });}
              }}
              editable={isEditing}
              placeholder={t('profile.enterEmail')}
              keyboardType="email-address"
              autoCapitalize="none"
              error={errors.mail || undefined}
              disabled={!isEditing}
              containerStyle={styles.inputGroup}
            />

            <Input
              value={formData.phone}
              onChangeText={text => {
                setFormData({ ...formData, phone: text });
                if (errors.phone) {setErrors({ ...errors, phone: '' });}
              }}
              editable={isEditing}
              placeholder={t('profile.enterPhone')}
              keyboardType="phone-pad"
              error={errors.phone || undefined}
              disabled={!isEditing}
              containerStyle={styles.inputGroup}
            />

            <View style={styles.genderSection}>
              <Text style={[styles.label, { textAlign: rtl.textAlign }]}>{t('profile.gender')}</Text>
              <ProfileGenderToggle
                colors={themeColors}
                value={formData.gender === 'female' ? 'female' : 'male'}
                onChange={gender => isEditing && setFormData({ ...formData, gender })}
                male_label={t('profile.male')}
                female_label={t('profile.female')}
                disabled={!isEditing}
                border_radius={DASH_STUDIO_RADIUS}
              />
            </View>

          <View style={[styles.nameRow, horizontalLtrDirectionStyle]}>
            <View style={styles.nameField}>
              <Input
                value={formData.height}
                onChangeText={text => setFormData({ ...formData, height: text })}
                editable={isEditing}
                placeholder={t('profile.heightPlaceholder')}
                keyboardType="numeric"
                disabled={!isEditing}
                containerStyle={styles.inputGroup}
              />
            </View>
            <View style={styles.nameField}>
              <Input
                value={formData.weight}
                onChangeText={text => setFormData({ ...formData, weight: text })}
                editable={isEditing}
                placeholder={t('profile.weightPlaceholder')}
                keyboardType="numeric"
                disabled={!isEditing}
                containerStyle={styles.inputGroup}
              />
            </View>
          </View>

            <Input
              value={formData.date_of_birth}
              onChangeText={text => setFormData({ ...formData, date_of_birth: text })}
              editable={isEditing}
              placeholder={t('profile.dateOfBirthPlaceholder')}
              disabled={!isEditing}
              containerStyle={styles.inputGroup}
            />
          </View>
        </View>

        {isEditing && (
          <Clickable onPress={handleSave} disabled={isLoading} accessibilityLabel={t('profile.saveChanges')} accessibilityRole="button">
            <View style={styles.saveButtonWrap}>
              <View style={[styles.saveButton, isLoading && styles.saveButtonDisabled]}>
              {isLoading ? <ButtonLoader size={16} color={themeColors.onPrimary} /> : <Text style={styles.saveButtonText}>{t('profile.saveChanges')}</Text>}
              </View>
            </View>
          </Clickable>
        )}

        <View style={[studio_card, styles.studioCard, styles.dangerZone]}>
          <Text style={styles.dangerZoneTitle}>{t('profile.accountActions')}</Text>

          <Clickable
            onPress={() => {
              Alert.alert(t('profile.deactivateTitle'), t('profile.deactivateMessage'), [
                { text: t('common.cancel'), style: 'cancel' },
                {
                  text: t('profile.deactivate'),
                  style: 'default',
                  onPress: async () => {
                    try {
                      setIsLoading(true);
                      const response: any = await deactivateAccount(user!.id);
                      if (response?.success) {
                        Alert.alert(t('profile.accountDeactivated'), t('profile.accountDeactivatedMessage'));
                        const { logout } = require('../shared/auth');
                        await logout();
                      } else {
                        Alert.alert(t('common.error'), response?.message || t('profile.failedToDeactivate'));
                      }
                    } catch (error: any) {
                      Alert.alert(t('common.error'), error?.message || t('profile.failedToDeactivate'));
                    } finally {
                      setIsLoading(false);
                    }
                  },
                },
              ]);
            }}
            accessibilityLabel={t('profile.deactivateAccount')}
            accessibilityRole="button">
            <View style={styles.deactivateAccountButtonWrap}>
              <View style={styles.deactivateAccountButton}>
                <SvgIcon name="freeze" size={20} color={themeColors.warning} />
                <Text style={styles.deactivateAccountText}>{t('profile.deactivateAccount')}</Text>
              </View>
            </View>
          </Clickable>

          <Clickable
            onPress={() => {
              Alert.alert(t('profile.deleteAccountTitle'), t('profile.deleteAccountMessage'), [
                { text: t('common.cancel'), style: 'cancel' },
                {
                  text: t('profile.delete'),
                  style: 'destructive',
                  onPress: () => {
                    Alert.alert(t('profile.confirmDeletionTitle'), t('profile.typeDeleteToConfirm'), [
                      { text: t('common.cancel'), style: 'cancel' },
                      {
                        text: t('profile.proceed'),
                        style: 'destructive',
                        onPress: async () => {
                          try {
                            setIsLoading(true);
                            const response: any = await deleteAccount(user!.id);
                            if (response?.success) {
                              Alert.alert(t('profile.accountDeleted'), t('profile.accountDeletedSuccess'));
                              const { logout } = require('../shared/auth');
                              await logout();
                            } else {
                              Alert.alert(t('common.error'), response?.message || t('profile.failedToDelete'));
                            }
                          } catch (error: any) {
                            Alert.alert(t('common.error'), error?.message || t('profile.failedToDelete'));
                          } finally {
                            setIsLoading(false);
                          }
                        },
                      },
                    ]);
                  },
                },
              ]);
            }}
            accessibilityLabel={t('profile.deleteAccountPermanently')}
            accessibilityRole="button">
            <View style={styles.deleteAccountButtonWrap}>
              <View style={styles.deleteAccountButton}>
                <SvgIcon name="trash" size={20} color={themeColors.error} />
                <Text style={styles.deleteAccountText}>{t('profile.deleteAccountPermanently')}</Text>
              </View>
            </View>
          </Clickable>
        </View>
      </Animated.ScrollView>
    </StackScreenShell>
  );
};
